import { NGXLogger } from 'ngx-logger';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject, from, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { Product } from '../../product/model/product';
import { AppConstants } from 'src/app/commons/app-constants';
import { MessageService } from 'primeng/api';
import { TransportUnitLiteDTO } from '../../shipments/shipments/shipments.component';
import { offlineDb, scopedKey } from 'src/app/offline/offline-db';
import { NetworkStatusService } from 'src/app/services/network-status.service';
import { OutboxService, generateProvisionalId, isProvisionalId } from 'src/app/offline/outbox.service';
import { isOfflineCapableNative } from 'src/app/commons/utils/platform';
import { noCachedCopyError, shouldFallBackToCache } from 'src/app/offline/detail-cache.service';
import { TallysheetListService } from '../services/tallysheet-list/tallysheet-list.service';

export interface TallyChartRow { length: number; girth: number; volume: number; }
// export interface TallyChartRow { length: number; girth: number; volume: number; }
export interface TallyChartRow {
  length: number;
  girth: number; // width for square
  thickness?: number;
  pieces?: number;
  volume: number;
}
export interface BinnedData { labels: string[]; counts: number[]; volumes: number[]; }
/** totalCbm/totalCft are GROSS; totalNetCbm/totalNetCft are NET (after allowances, null on an old server). */
export interface TallySummary { totalPieces: number; totalCbm: number; totalCft: number; totalNetCbm?: number | null; totalNetCft?: number | null; }


@Injectable({
  providedIn: 'root'
})
export class TransportUnitService {
  private readonly logger = inject(NGXLogger);


  public apiUrl = AppConstants.API_URL + 'api/v1';

  private transportUnitDataSubject = new BehaviorSubject<any>(null);
  data$ = this.transportUnitDataSubject.asObservable();

  /** 2026-10-13: this Stock Unit changed on the server (a save, a lock, a receive, a live change from another device).
   *  Carries nothing: each part re-reads what it shows, quietly and in place. Deliberately NOT data$: re-emitting data$
   *  makes the grids and the header form re-bind, and could overwrite what someone is typing. */
  private readonly unitChangedSubject = new Subject<void>();
  readonly unitChanged$ = this.unitChangedSubject.asObservable();
  notifyUnitChanged(): void { this.unitChangedSubject.next(); }

  constructor(private http: HttpClient,
    private messageService: MessageService,
    private networkStatusService: NetworkStatusService,
    private outboxService: OutboxService,
    private tallysheetListService: TallysheetListService,
  ) { }

 

  getTransportModes(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/transport-modes/grouped`); // Fetch from DB
  }

  getById(id: number): Observable<any> {
    const cacheKey = `tu:id:${id}`;
    return this.http.get<any>(`${this.apiUrl}/transportunits/${id}`).pipe(
      tap((data) => { if (isOfflineCapableNative()) offlineDb.detailCache.put({ key: cacheKey, data, fetchedAt: Date.now() }); }),
      catchError((err) => (isOfflineCapableNative() && shouldFallBackToCache(err) ? this.readDetailCache(cacheKey, err) : throwError(() => err))),
    );
  }

  // forTrace (2026-09-03 gating audit): set ONLY by the Full Trace Stock Unit quick-view popover
  // — every other caller (the Stock Unit edit form, etc.) omits it and gets today's unchanged,
  // unredacted response. The backend strips supplier/buyer for a caller without FINANCE_ACCESS
  // only when this flag is present. Uses a DISTINCT cache key from the normal
  // `tu:encoded:${encodedId}` one — the redacted (trace) and unredacted (edit form) payloads for
  // the same TU must never collide/overwrite each other in the offline cache if both are fetched
  // on the same device.
  getByEncodedId(encodedId: string, forTrace = false): Observable<any> {
    this.logger.debug('[OPAQUE-ID][TU Service] Fetching by encodedId:', encodedId);
    const cacheKey = forTrace ? `tu:encoded:${encodedId}:trace` : `tu:encoded:${encodedId}`;
    const params = forTrace ? new HttpParams().set('fromFullTrace', 'true') : undefined;
    return this.http.get<any>(`${this.apiUrl}/transportunits/by-encoded/${encodeURIComponent(encodedId)}`, { params }).pipe(
      tap((data) => { if (isOfflineCapableNative()) offlineDb.detailCache.put({ key: cacheKey, data, fetchedAt: Date.now() }); }),
      catchError((err) => (isOfflineCapableNative() && shouldFallBackToCache(err) ? this.readDetailCache(cacheKey, err) : throwError(() => err))),
    );
  }

  // Write-through cache: whatever a TU detail view successfully fetches online is stashed
  // here, so re-opening the same TU while offline falls back to the last-seen copy instead
  // of a network error. Not a full sync — only TUs you've actually viewed are available.
  private readDetailCache(cacheKey: string, original?: unknown): Observable<any> {
    return from(offlineDb.detailCache.get(cacheKey)).pipe(
      switchMap((entry) => entry
        ? of(entry.data)
        : throwError(() => noCachedCopyError(cacheKey, original))),
    );
  }

  create(data: any): Observable<any> {
    if (isOfflineCapableNative() && !this.networkStatusService.isOnline) {
      return from(this.createOffline(data));
    }
    return this.http.post<any>(`${this.apiUrl}/transportunits`, data);
  }

  // By the time this runs, TransportUnitComponent.proceedWithSubmit() has already stripped
  // product/loadingSite/transportMode down to bare id refs ({productId: X}, no name) — correct
  // for the real POST body, but useless for anything that wants to *display* this TU before it
  // syncs (the tallysheet header, the TU list's pending-sync row). Re-resolve full display
  // objects from the same offline masterCache the autocomplete used to offer these choices in
  // the first place — they're guaranteed present, since that's how the user picked them.
  private async resolveDisplayRefs(data: any): Promise<{ product: any; loadingSite: any; transportMode: any }> {
    const [products, loadingSites, transportModes] = await Promise.all([
      offlineDb.masterCache.get(scopedKey('products')),
      offlineDb.masterCache.get(scopedKey('loadingSites')),
      offlineDb.masterCache.get(scopedKey('transportModes')),
    ]);
    const productId = data?.product?.productId ?? data?.product?.id;
    const loadingSiteId = data?.loadingSite?.loadingSiteId;
    const transportModeId = data?.transportMode?.id;
    const product = ((products?.data as any[]) || []).find((p) => p.productId === productId) ?? data?.product ?? null;
    const loadingSite = ((loadingSites?.data as any[]) || []).find((l) => l.loadingSiteId === loadingSiteId) ?? data?.loadingSite ?? null;
    const transportMode = ((transportModes?.data as any[]) || []).find((m) => m.id === transportModeId) ?? data?.transportMode ?? null;
    return { product, loadingSite, transportMode };
  }

  // Fabricates a response shaped exactly like a real TransportUnitResponseDTO (see
  // TransportUnitServiceImpl.createTransportUnit / TransportUnitResponseDTO on the backend)
  // so every downstream consumer of the create response — handleSubmitSuccess, the
  // TransportUnitService.data$ subscribers that bind the tallysheet grid, and the
  // query-param-triggered getByEncodedId() refetch that follows — works completely
  // unmodified, believing this is a real server response. The actual create is queued in
  // the outbox for the (not yet built) sync engine to replay once back online.
  private async createOffline(data: any): Promise<any> {
    const clientUuid = uuidv4();
    const provisionalId = generateProvisionalId();
    const provisionalTallyMasterId = generateProvisionalId();
    const provisionalEncodedId = `offline-${clientUuid}`;

    const { product, loadingSite, transportMode } = await this.resolveDisplayRefs(data);

    // Backend request field is `lines[]`; response field is `additionalLines[]` — mirror
    // that rename here so patchTransportUnitForm/dashboard's tallysheetLines getter (which
    // reads response.additionalLines[].tallyMasterId) see the same shape as a real response.
    const additionalLines = (data.lines || []).map((line: any) => ({
      ...line,
      tallyMasterId: generateProvisionalId(),
    }));

    // Mirrors TransportUnitServiceImpl's flexibleIntent check (Java) exactly — Flexible Layout
    // is Square-only and deliberately leaves `product` blank, so falling back to "product's own
    // type, else ROUND" (the pre-existing rule) would silently fabricate a ROUND TallyMaster and
    // strand the user's Flexible choice on the Round grid, which has no Flexible support at all.
    const flexibleIntent = data.transactionType === 'SELL' && data.gridStyle === 'FLEXIBLE';

    const fakeResponse = {
      ...data,
      product,
      loadingSite,
      transportMode,
      id: provisionalId,
      encodedId: provisionalEncodedId,
      tallyMasterId: provisionalTallyMasterId,
      tallyMaster: {
        id: provisionalTallyMasterId,
        types: flexibleIntent ? 'SQUARE' : (product?.types ?? 'ROUND'),
      },
      totalTallyDetailRecords: 0,
      additionalLines,
      inventoryStatus: data?.inventoryStatus ?? null,
      locked: false,
      // Not part of the real DTO — internal marker so UI code can show an "unsynced" badge
      // later if needed. Harmless extra field for anything that doesn't look for it.
      _offlineClientUuid: clientUuid,
    };

    // Seed the detail cache under the exact keys getById/getByEncodedId look up, so the
    // second fetch triggered by exposeObfuscatedIdInUrl's query-param navigation (which
    // clears then re-fetches via getByEncodedId) resolves from cache instead of erroring —
    // without this, the tallysheet would flash in then immediately clear.
    offlineDb.detailCache.put({ key: `tu:id:${provisionalId}`, data: fakeResponse, fetchedAt: Date.now() });
    offlineDb.detailCache.put({ key: `tu:encoded:${provisionalEncodedId}`, data: fakeResponse, fetchedAt: Date.now() });
    TallysheetListService.rememberProvisionalOwner(fakeResponse); // quick line → transport unit lookup (Phase 2)
    // clientUuid rides along in the actual POST payload (backend idempotency key — see
    // TransportUnitServiceImpl.createTransportUnit). The two _offline* fields are for
    // SyncEngineService's own use when it replays this op: they're how it knows which
    // provisional tallyMasterId (line 1, additional lines by ordinal) to map to which real id
    // in the response, so already-queued SAVE_TALLY_DETAILS ops for this TU can be rewritten.
    // Stripped before the request is sent (see SyncEngineService.replayCreateTu).
    const outboxPayload = {
      ...data,
      clientUuid,
      _offlineProvisionalTallyMasterId: provisionalTallyMasterId,
      _offlineProvisionalLineTallyMasterIds: additionalLines.map((l: any) => l.tallyMasterId),
    };

    await this.outboxService.enqueue('CREATE_TU', clientUuid, outboxPayload);
    return fakeResponse;
  }

  update(id: number, data: any): Observable<any> {
    // A provisional id means this TU hasn't been created on the server yet — even if we're
    // back online, there's nothing to PUT to until the sync engine replays the queued
    // create. Route this edit into the still-pending create payload instead.
    if (isOfflineCapableNative() && isProvisionalId(id)) {
      return from(this.updateOffline(id, data));
    }
    return this.http.put<any>(`${this.apiUrl}/transportunits/${id}`, data);
  }

  private async updateOffline(id: number, data: any): Promise<any> {
    const cacheKey = `tu:id:${id}`;
    const entry = await offlineDb.detailCache.get(cacheKey);
    if (!entry) {
      throw new Error(`No offline transport unit found for id ${id}`);
    }
    const existing = entry.data as any;
    const clientUuid = existing._offlineClientUuid;
    // data's product/loadingSite/transportMode are stripped to bare id refs (see
    // resolveDisplayRefs) — re-resolve full display objects same as createOffline, so an
    // edit that changes the product doesn't blank out its name in the pending-sync display.
    const { product, loadingSite, transportMode } = await this.resolveDisplayRefs(data);
    const updatedResponse = {
      ...existing,
      ...data,
      product,
      loadingSite,
      transportMode,
      id: existing.id,
      encodedId: existing.encodedId,
      tallyMasterId: existing.tallyMasterId,
      tallyMaster: existing.tallyMaster,
      additionalLines: existing.additionalLines,
      _offlineClientUuid: clientUuid,
    };
    await offlineDb.detailCache.put({ key: cacheKey, data: updatedResponse, fetchedAt: Date.now() });
    await offlineDb.detailCache.put({ key: `tu:encoded:${existing.encodedId}`, data: updatedResponse, fetchedAt: Date.now() });
    await this.mergeIntoQueuedCreatePayload(clientUuid, data);
    return updatedResponse;
  }

  // Edits made before the create has synced are folded directly into the still-pending
  // CREATE_TU outbox payload — queuing a separate UPDATE op would be meaningless, since
  // there's no server-side row yet for it to target.
  private async mergeIntoQueuedCreatePayload(clientUuid: string, data: any): Promise<void> {
    const ops = await this.outboxService.getByClientUuid(clientUuid);
    const createOp = ops.find((op) => op.opType === 'CREATE_TU' && op.status === 'pending');
    if (createOp) {
      createOp.payload = { ...(createOp.payload as any), ...data };
      await offlineDb.outbox.put(createOp);
    }
  }

  // A provisional id means this TU was created offline and its CREATE_TU op may not have synced
  // yet — that record was NEVER created on the server, so a live DELETE against its negative id
  // 500s there (findById().orElseThrow(), same failure class as any other id the server has
  // never heard of). This check is deliberately on isProvisionalId alone, not gated on
  // isOnline: the exact failure this fixes happened WHILE online, in the race window between
  // reconnecting and the sync engine actually replaying the still-queued CREATE_TU — a
  // provisional id is never valid to send live, regardless of current connectivity.
  delete(id: number): Observable<any> {
    if (isOfflineCapableNative() && isProvisionalId(id)) {
      return from(this.deleteOffline(id));
    }
    return this.http.delete<any>(`${this.apiUrl}/transportunits/${id}`);
  }

  // Cancels a not-yet-synced offline-created TU entirely: every queued outbox op for it
  // (CREATE_TU + any dependent UPDATE_GRID_STYLE/SAVE_TALLY_DETAILS etc., all sharing the same
  // clientUuid) is removed so nothing gets replayed later into a resurrected record, its
  // per-clientUuid queued tally rows (pendingRows) are dropped, and its detail-cache entries are
  // purged so no other view can still resolve stale data for it. No live HTTP call at all —
  // there is nothing on the server for a provisional id to reference.
  private async deleteOffline(id: number): Promise<any> {
    const cacheKey = `tu:id:${id}`;
    const entry = await offlineDb.detailCache.get(cacheKey);
    const clientUuid = (entry?.data as any)?._offlineClientUuid;
    if (clientUuid) {
      await this.outboxService.cancelByClientUuid(clientUuid);
      await offlineDb.pendingRows.delete(clientUuid);
    }
    await offlineDb.detailCache.delete(cacheKey);
    const encodedId = (entry?.data as any)?.encodedId;
    if (encodedId) {
      await offlineDb.detailCache.delete(`tu:encoded:${encodedId}`);
    }
    return {};
  }

  /** Who locked it is resolved server-side from the authenticated principal — never trust a
   *  client-supplied identity for an audit field. */
  lockTransportUnit(id: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/transportunits/${id}/lock`, {});
  }

  /** 2026-09-27 — read right before the Lock confirm dialog opens, to warn (never block) when no
   *  photos have been uploaded for this Stock Unit yet. */
  hasAnyPhotos(id: number): Observable<{ hasPhotos: boolean }> {
    return this.http.get<{ hasPhotos: boolean }>(`${this.apiUrl}/transportunits/${id}/has-photos`);
  }

  /** Inventory V2 — "delete this line's tallysheet" for line 1: deletes its tally rows and
   *  clears product/origin/quality immediately (line 1 can't defer this to the next save the
   *  way additional lines can, since `product` is a required field). */
  clearLine1(id: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/transportunits/${id}/line1/clear`, {});
  }

  /** Immediate removal of an additional line (2+) — deletes its TallyMaster + any TallyDetail
   *  rows right now, instead of the old deferred-to-next-save path that silently discarded the
   *  pending removal if the user refreshed/navigated away before clicking Update. */
  removeAdditionalLine(transportUnitId: number, tallyMasterId: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/transportunits/${transportUnitId}/lines/${tallyMasterId}/clear`, {});
  }

  /** Persists Flexible View's Classic/Flexible choice for one tally — called only when the user
   *  actually switches views. Previously live-only, so choosing a view while offline was silently
   *  lost (the toggle "stuck" for that session but reverted to whatever synced last on next load) —
   *  now queued the same way SAVE_TALLY_SETTINGS already is. */
  updateGridStyle(tallyMasterId: number, gridStyle: 'CLASSIC' | 'FLEXIBLE'): Observable<any> {
    if (isOfflineCapableNative() && (isProvisionalId(tallyMasterId) || !this.networkStatusService.isOnline)) {
      return this.updateGridStyleOffline(tallyMasterId, gridStyle);
    }
    return this.http.patch<any>(`${this.apiUrl}/tally/master/${tallyMasterId}/grid-style`, {}, { params: { gridStyle } }).pipe(
      // Server unreachable while the phone says "online" — queue it (OFFLINE_PLAN.md 1.4); safe to repeat.
      catchError((err) => isOfflineCapableNative() && err?.status === 0
        ? this.updateGridStyleOffline(tallyMasterId, gridStyle)
        : throwError(() => err)),
    );
  }

  // Same dependency convention as TallysheetListService.saveSettingsOffline: a provisional
  // masterId means the owning TU was itself created offline and hasn't synced yet, so the op is
  // linked to that TU's clientUuid (idMap resolves the real masterId once CREATE_TU syncs). An
  // already-real masterId (device just happens to be offline) gets its own synthetic per-master
  // clientUuid instead, since there's no owning create to depend on.
  private updateGridStyleOffline(tallyMasterId: number, gridStyle: 'CLASSIC' | 'FLEXIBLE'): Observable<any> {
    const offlineMaster = isProvisionalId(tallyMasterId);
    const clientUuid$ = offlineMaster
      ? from(this.tallysheetListService.findClientUuidForProvisionalMaster(tallyMasterId))
      : of(`gridstyle-${tallyMasterId}`);
    return clientUuid$.pipe(
      switchMap((clientUuid) => {
        if (!clientUuid) {
          this.logger.warn('No offline transport unit found for provisional master — grid style change dropped', tallyMasterId);
          return of({ gridStyle });
        }
        return from(this.outboxService.enqueue('UPDATE_GRID_STYLE', clientUuid, { tallyMasterId, gridStyle })).pipe(
          map(() => ({ gridStyle })),
        );
      }),
    );
  }

  getProducts(): Observable<Product[]> {
    return this.http.get<Product[]>(AppConstants.API_URL + 'api/v1/products');
  }

  searchSeaPort(query: string): Observable<any[]> {
    const url = `${AppConstants.API_URL}api/v1/sea-ports/search-query1?query=${query}`;
    this.logger.debug(`Url is ${url}`)
    if (!query.trim()) {
      return of([]); // Return an empty observable if query is blank or whitespace
    }
    return this.http.get<any[]>(url);
  }

  checkUniqueness(transportId: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/transportunits/check-uniqueness?transportId=${transportId}`);
  }

  showSuccess(msg: string) {
    this.messageService.add({ severity: 'success', summary: 'Success', detail: msg });
  }

  showInfo(msg: string) {
    this.messageService.add({ severity: 'info', summary: 'Info', detail: msg });
  }

  showWarn(msg: string) {
    this.messageService.add({ severity: 'warn', summary: 'Warn', detail: msg });
  }

  showError(msg: string) {
    this.messageService.add({ severity: 'error', summary: 'Error', detail: msg });
  }

  private seed = 12345;
  private rnd() { this.seed = (this.seed * 16807) % 2147483647; return (this.seed % 10000) / 10000; }

  generateDummy(n = 1000): TallyChartRow[] {
    const arr: TallyChartRow[] = [];
    for (let i = 0; i < n; i++) {
      const length = Math.round((1 + this.rnd() * 7) * 10) / 10; // 1 - 8 m
      const girth = Math.round((10 + this.rnd() * 90) * 10) / 10; // 10 - 100 cm
      const diameter = girth / Math.PI;
      const volume = Math.round((Math.PI * (diameter ** 2) / 4 * length) * 1000) / 1000;
      arr.push({ length, girth, volume });
    }
    return arr;
  }

  // Replace with real HTTP call in production:
  // constructor(private http: HttpClient) {}
  // getTallyRows() { return this.http.get<TallyRow[]>('/api/tally'); }
  getTallyRows(): Observable<TallyChartRow[]> { return of(this.generateDummy(1000)); }

  // getChartData(tallyMasterId : number): Observable<TallyChartRow[]> { return this.http.get<TallyChartRow[]>(`${this.apiUrl}/tally/chart-data`); }

  getChartData(tallyMasterId?: number): Observable<TallyChartRow[]> {
    // It's often better to use console.log for debugging instead of alert
    this.logger.info(`Fetching chart data for tallyMasterId: ${tallyMasterId}`);

    // Return an empty observable if the ID is not valid
    if (!tallyMasterId) {
      return of([]);
    }

    // The HttpParams logic is no longer needed.
    // The ID is now appended directly to the URL.
    const url = `${this.apiUrl}/tally/chart-data/${tallyMasterId}`;
    const cacheKey = `tallyChart:${tallyMasterId}`;

    return this.http.get<TallyChartRow[]>(url).pipe(
      tap((data) => { if (isOfflineCapableNative()) offlineDb.detailCache.put({ key: cacheKey, data, fetchedAt: Date.now() }); }),
      catchError((err) => (isOfflineCapableNative() && shouldFallBackToCache(err) ? this.readDetailCache(cacheKey, err) : throwError(() => err))),
    );
  }
  /* getGirthHistogram(bins = 10): Observable<BinnedData> { return this.http.get<BinnedData>(`${this.apiUrl}/tally/histogram/girth?bins=${bins}`); }
 
   getLengthHistogram(bins = 10): Observable<BinnedData> { return this.http.get<BinnedData>(`${this.apiUrl}/tally/histogram/length?bins=${bins}`); }
 
   getSummary(): Observable<TallySummary> { return this.http.get<TallySummary>(`${this.apiUrl}/tally/summary`); }*/

  getGirthHistogram(bins = 10, tallyMasterId?: number): Observable<BinnedData> {
    if (!tallyMasterId) return of([] as any);
    return this.http.get<BinnedData>(`${this.apiUrl}/tally/histogram/girth/${bins}/${tallyMasterId}`);
  }

  getLengthHistogram(bins = 10, tallyMasterId?: number): Observable<BinnedData> {
    if (!tallyMasterId) return of([] as any);
    return this.http.get<BinnedData>(`${this.apiUrl}/tally/histogram/length/${bins}/${tallyMasterId}`);
  }

  getSummary(tallyMasterId?: number): Observable<TallySummary | null> {
    if (!tallyMasterId) return of(null);
    const cacheKey = `tallySummary:${tallyMasterId}`;
    return this.http.get<TallySummary>(`${this.apiUrl}/tally/summary/${tallyMasterId}`).pipe(
      tap((data) => { if (isOfflineCapableNative()) offlineDb.detailCache.put({ key: cacheKey, data, fetchedAt: Date.now() }); }),
      catchError((err) => (isOfflineCapableNative() && shouldFallBackToCache(err) ? this.readDetailCache(cacheKey, err) : throwError(() => err))),
    );
  }

  getWidthHistogram(bins: number, tallyMasterId: number): Observable<BinnedData> {
    const url = `${this.apiUrl}/tally/histogram/width/${tallyMasterId}?bins=${bins}`;
    this.logger.debug(`getWidthHistogram URL: ${url}`);
    return this.http.get<BinnedData>(url);
  }

  getSawnSquareSummary(tallyMasterId: number): Observable<any> {
    const cacheKey = `tallySawnSquare:${tallyMasterId}`;
    return this.http.get<any>(`${this.apiUrl}/tally/sawn-square-summary/${tallyMasterId}`).pipe(
      tap((data) => { if (isOfflineCapableNative()) offlineDb.detailCache.put({ key: cacheKey, data, fetchedAt: Date.now() }); }),
      catchError((err) => (isOfflineCapableNative() && shouldFallBackToCache(err) ? this.readDetailCache(cacheKey, err) : throwError(() => err))),
    );
  }

searchAvailableUnits(query: string, shipmentType?: string): Observable<TransportUnitLiteDTO[]> {

  const url = `${this.apiUrl}/transportunits/search-available`;
  
    this.logger.debug('TransportUnitService: Starting searchAvailableUnits - query:', query, 'shipmentType:', shipmentType);
    if (!query || !query.trim()) {
      this.logger.debug('TransportUnitService: Query is empty, returning empty array');
      return new Observable(obs => obs.next([]));
    }
    let params = new HttpParams().set('query', query.trim());
    if (shipmentType) {
      params = params.set('shipmentType', shipmentType);
    }
    this.logger.debug('TransportUnitService: Making HTTP GET to', url, 'with params:', params.toString());
    const req = this.http.get<TransportUnitLiteDTO[]>(url, { params });
    req.subscribe({
      next: (res) => {
        this.logger.info('TransportUnitService: HTTP response received:', res);
        this.logger.debug('TransportUnitService: Response length:', res.length);
        if (res.length > 0) {
          this.logger.debug('TransportUnitService: First item:', res[0]);
        }
      },
      error: (err) => this.logger.error('TransportUnitService: HTTP error:', err)
    });
    return req;
  }
  initReconciliation(tuId: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/transportunits/${tuId}/reconciliation/init`, {});
  }

  saveManualReconciliation(tuId: number, pieces: number, cbm: number): Observable<any> {
    return this.http.patch(`${this.apiUrl}/transportunits/${tuId}/reconciliation/manual`, { pieces, cbm });
  }

  setTransportUnitData(data: any) {
    this.transportUnitDataSubject.next(data);
  }

  clearTransportUnitData(): void {
    this.transportUnitDataSubject.next(null);
  }
}
